import * as z from 'zod/mini';
import { cableDropSchema } from './cable-drop.ts';
import { cableReadingSchema } from './cable-reading.ts';
import { clientSchema } from './client.ts';
import { pathSchema } from './common.ts';
import { credentialStatusSchema } from './credentials.ts';
import { itemSchema } from './item.ts';
import { pathwaySchema } from './pathway.ts';
import { floorSchema } from './plan-floor.ts';
import { portGroupSchema } from './port-group.ts';
import { propertySchema } from './property.ts';
import { rackReservationSchema } from './rack-reservation.ts';
import { rackSchema } from './rack.ts';
import { roomSchema } from './room.ts';

// Response envelopes for the tree API. Every GET of a node returns the node,
// its direct children, and the path from the client down to it so the UI
// can render a breadcrumb without extra requests.

/** What a client holds, for the client list: counts across all its properties. */
export const clientSummarySchema = z.object({
  properties: z.int(),
  /** How many of the properties are homes; decides the client's icon. */
  residential: z._default(z.int(), 0),
  rooms: z.int(),
  items: z.int(),
  drops: z.int(),
});
export type ClientSummary = z.infer<typeof clientSummarySchema>;

const EMPTY_SUMMARY: ClientSummary = {
  properties: 0,
  residential: 0,
  rooms: 0,
  items: 0,
  drops: 0,
};

export const clientListEntrySchema = z.object({
  ...clientSchema.shape,
  summary: z._default(clientSummarySchema, EMPTY_SUMMARY),
});
export type ClientListEntry = z.infer<typeof clientListEntrySchema>;

export const clientListSchema = z.object({ clients: z.array(clientListEntrySchema) });
export type ClientList = z.infer<typeof clientListSchema>;

export const clientDetailSchema = z.object({
  client: clientSchema,
  properties: z.array(propertySchema),
  path: pathSchema,
});
export type ClientDetail = z.infer<typeof clientDetailSchema>;

export const propertyDetailSchema = z.object({
  property: propertySchema,
  rooms: z.array(roomSchema),
  path: pathSchema,
  /** Whether sealed access codes exist for the property; never the values. */
  access: z._default(credentialStatusSchema, { stored: false, updatedAt: null }),
});
export type PropertyDetail = z.infer<typeof propertyDetailSchema>;

export const roomDetailSchema = z.object({
  room: roomSchema,
  rooms: z.array(roomSchema),
  racks: z.array(rackSchema),
  items: z.array(itemSchema),
  drops: z.array(cableDropSchema),
  path: pathSchema,
});
export type RoomDetail = z.infer<typeof roomDetailSchema>;

export const rackDetailSchema = z.object({
  rack: rackSchema,
  items: z.array(itemSchema),
  /** The rack's reserved unit ranges (ROADMAP A11); [] when none are held. */
  reservations: z._default(z.array(rackReservationSchema), []),
  path: pathSchema,
});
export type RackDetail = z.infer<typeof rackDetailSchema>;

/** Reply of POST /api/items/:id/move: the item, and how many links the move broke. */
/**
 * A move's or an edit's reply: the item, how many links the change could no
 * longer keep, and how many power feeds went with it (an edit that leaves a
 * source with fewer outlets; a move never breaks a feed, since it stays
 * inside the property, so that reply omits the count and it reads as 0).
 */
export const itemMoveResultSchema = z.object({
  item: itemSchema,
  brokenLinks: z.int(),
  brokenFeeds: z._default(z.int(), 0),
});

/** Reply of PUT /api/racks/:id/order: the rack's items in their new order. */
export const rackItemsSchema = z.object({ items: z.array(itemSchema) });

/** Reply of GET /api/properties/:id/inventory: every item of the property. */
export const inventorySchema = z.object({ items: z.array(itemSchema) });

export const itemDetailSchema = z.object({
  item: itemSchema,
  path: pathSchema,
  /** Whether hidden credentials exist for the item; never the values. */
  credentials: z._default(credentialStatusSchema, { stored: false, updatedAt: null }),
});
export type ItemDetail = z.infer<typeof itemDetailSchema>;

export const dropDetailSchema = z.object({
  drop: cableDropSchema,
  fromNodeName: z.nullable(z.string()),
  toNodeName: z.nullable(z.string()),
  path: pathSchema,
  /** The run's tester readings, newest first (ROADMAP I9). */
  readings: z._default(z.array(cableReadingSchema), []),
  /** The pathways this run threads (ROADMAP I10). */
  pathways: z._default(z.array(pathwaySchema), []),
});
export type DropDetail = z.infer<typeof dropDetailSchema>;

/** Reply of the reading routes: the run's readings, and the run they rolled up into. */
export const dropReadingsSchema = z.object({
  readings: z.array(cableReadingSchema),
  drop: z.nullable(cableDropSchema),
});

export const clientEnvelopeSchema = z.object({ client: clientSchema });
export const propertyEnvelopeSchema = z.object({ property: propertySchema });
export const roomEnvelopeSchema = z.object({ room: roomSchema });
export const rackEnvelopeSchema = z.object({ rack: rackSchema });
export const itemEnvelopeSchema = z.object({ item: itemSchema });
export const dropEnvelopeSchema = z.object({ drop: cableDropSchema });
/** Reply of POST /api/rooms/:id/drops/batch: every run the pattern made, in order. */
export const dropBatchResultSchema = z.object({ drops: z.array(cableDropSchema) });

/** What a delete would remove, shown before confirming. */
export const cascadeCountsSchema = z.object({
  properties: z.int(),
  rooms: z.int(),
  racks: z.int(),
  items: z.int(),
  drops: z.int(),
  photos: z.int(),
  /** Checklists on the properties beneath; they go with their property. */
  checklists: z.int(),
});
export type CascadeCounts = z.infer<typeof cascadeCountsSchema>;

// The full tree of one client, nested, for the desktop sidebar and reports.
export interface RoomTree {
  readonly room: z.infer<typeof roomSchema>;
  readonly rooms: readonly RoomTree[];
  readonly racks: readonly {
    readonly rack: z.infer<typeof rackSchema>;
    readonly items: readonly z.infer<typeof itemSchema>[];
  }[];
  readonly items: readonly z.infer<typeof itemSchema>[];
  readonly drops: readonly z.infer<typeof cableDropSchema>[];
}

export interface PropertyTree {
  readonly property: z.infer<typeof propertySchema>;
  readonly rooms: readonly RoomTree[];
  /**
   * The property's floors, ground-up (ROADMAP D1). A room sits on one of them,
   * so the tree and the property's rooms list head one group per floor without
   * a second request. Optional so a tree built before D1 — a test fixture, a
   * cached offline payload — reads as a property with no floors drawn.
   */
  readonly floors?: readonly z.infer<typeof floorSchema>[];
}

export interface ClientTree {
  readonly client: z.infer<typeof clientSchema>;
  readonly properties: readonly PropertyTree[];
  /**
   * The client's LAG and mirror groups (ROADMAP C5), flat across every
   * device: the trace, the topology, and the device page read them from here.
   * Optional so a tree built before C5 (a test fixture, an older payload)
   * reads as having none.
   */
  readonly portGroups?: readonly z.infer<typeof portGroupSchema>[];
}

const rackTreeSchema = z.object({ rack: rackSchema, items: z.array(itemSchema) });

export const roomTreeSchema: z.ZodMiniType<RoomTree> = z.object({
  room: roomSchema,
  get rooms() {
    return z.array(roomTreeSchema);
  },
  racks: z.array(rackTreeSchema),
  items: z.array(itemSchema),
  drops: z.array(cableDropSchema),
});

export const clientTreeSchema: z.ZodMiniType<ClientTree> = z.object({
  client: clientSchema,
  properties: z.array(
    z.object({
      property: propertySchema,
      rooms: z.array(roomTreeSchema),
      floors: z._default(z.array(floorSchema), []),
    }),
  ),
  portGroups: z._default(z.array(portGroupSchema), []),
});
